import {loadStripe, Stripe, StripeElements} from '@stripe/stripe-js';
import {PaymentConfig, TaxInfo} from '../types';
import {BASE_URLS} from './PaymentSDKBase';
import {isBase64String} from './UIUtilities';

export interface StripeServiceCallbacks {
    onSuccess?: (data: unknown) => void;
    onError?: (error: { message: string }) => void;
    onLoading?: (isLoading: boolean) => void;
}

export interface ApiResponse {
    verify: string;
    id?: string;

    [key: string]: unknown;
}

export interface StripeElementsConfig {
    theme?: 'flat' | 'stripe';
    layout?: {
        type: 'tabs' | 'accordion';
        defaultCollapsed?: boolean;
        radios?: boolean;
        spacedAccordionItems?: boolean;
        visibleAccordionItemsCount?: number;
    };
    wallets?: {
        applePay?: 'auto' | 'never';
        googlePay?: 'auto' | 'never';
        link?: 'auto' | 'never';
    };
    business?: {
        name?: string;
    };
    fields?: {
        billingDetails?: {
            email?: 'auto' | 'never';
            phone?: 'auto' | 'never';
            address?: {
                country?: 'auto' | 'never';
                line1?: 'auto' | 'never';
                line2?: 'auto' | 'never';
                city?: 'auto' | 'never';
                state?: 'auto' | 'never';
                postalCode?: 'auto' | 'never';
            };
        };
    };
}

export class StripeService {
    private config: PaymentConfig;
    private stripe: Stripe | null = null;
    private elements: StripeElements | null = null;
    private clientSecret: string | null = null;
    private publishableKey: string | null = null;
    private id: string | null = null;
    private apiResponse: ApiResponse | null = null;
    private currency: string | null = null;
    private amount: number | null = null;
    private beforeDiscountAmount: number | null = null;
    private firstCycleAmount: number | null = null;
    private type: string | null = null;
    private isTrial: boolean | null = null;
    private nextBillingDate: number | null = null;
    /** JSON.stringify(tax_info) last applied via checkout-tax-info API */
    private lastCheckoutTaxSynced: string | null = null;

    constructor(config: PaymentConfig) {
        this.config = config;
    }

    /**
     * Fetch Stripe publishable key from the API
     */
    async fetchPublishableKey(): Promise<string> {
        if (this.publishableKey) {
            return this.publishableKey;
        }

        try {
            const apiUrl = this.getApiUrl('payments/payment-methods-enabled');
            const response = await fetch(apiUrl);

            // Check if response is ok before trying to parse JSON
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to fetch publishable key`);
            }

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                console.error('Invalid JSON response:', jsonError);
                throw new Error('Invalid JSON response from API');
            }

            this.publishableKey = data.client_keys?.stripe;

            if (!this.publishableKey) {
                throw new Error('Stripe publishable key not found in response');
            }

            return this.publishableKey;
        } catch (error) {
            console.error('Failed to fetch publishable key:', error);
            throw new Error('Failed to fetch Stripe publishable key from API');
        }
    }

    /**
     * Fetch client secret from the payment API
     */
    async fetchClientSecret(
        config: PaymentConfig,
        paymentGatewayType: 'stripe_js' | 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p' = 'stripe_js',
        bypassCache = false
    ): Promise<string | null> {
        if (!bypassCache && this.clientSecret && paymentGatewayType === 'stripe_js') {
            return this.clientSecret;
        }
        const apiUrl = this.getPaymentUrl();
        const {authToken, authHeaderName} = this.getAuthTokenAndHeader(config);
        // Generate random checksum
        const generateRandomChecksum = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < 16; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        };

        const body: Record<string, any> = {
            order_id: config.order_id,
            email: config.email,
            verify: {
                version: "2",
                checksum: generateRandomChecksum(),
                option: "-"
            },
            payment_gateway: {
                type: paymentGatewayType
            }
        }
        // Multi-product (products array) or single product (product_id) — mutually exclusive
        if (config.products && config.products.length > 0) {
            body['products'] = config.products;
        } else {
            body['product_id'] = config.product_id;
        }
        if (config.product_id === 'dynamic') {
            body['amount'] = config.amount;
            body['currency'] = config.currency;
        }
        if (config.metadata !== undefined && config.metadata !== null) {
            body['metadata'] = config.metadata;
        }
        // Add all input param from config if available
        if (config.source_url) {
            body['source_url'] = config.source_url;
        } else {
            body['source_url'] = window.location.href;
        }
        if (config.site_code) {
            body['site_code'] = config.site_code;
        } else if (window.location.href) {
            const url = new URL(window.location.href);
            const pathSegments = url.pathname.split('/').filter(segment => segment.length > 0);
            if (pathSegments.length > 0) {
                body['site_code'] = pathSegments[pathSegments.length - 1];
            }
        }
        if (config.promotion_code) {
            body['promotion_code'] = config.promotion_code;
        }
        if (config.promotion_token) {
            body['promotion_token'] = config.promotion_token;
        }
        if (config.upgrade !== undefined) {
            body['upgrade'] = config.upgrade;
        }
        if (config.phone) {
            body['phone'] = config.phone;
        }
        if (config.grant_option) {
            body['grant_option'] = config.grant_option;
        }
        if (config.name) {
            body['name'] = config.name;
        }
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'accept': 'application/json',
                [authHeaderName]: authToken,
                'store': 'stripe',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body),
        });
        // Check if response is ok before trying to parse JSON
        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: Failed to fetch client secret`;

            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.details) {
                    errorMessage = errorData.details;
                } else {
                    errorMessage = `${errorMessage} - ${JSON.stringify(errorData)}`;
                }
            } catch {
                // If we can't parse the error response, get the text
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = `${errorMessage} - ${errorText}`;
                    }
                } catch {
                    // Keep the original error message if we can't get any response data
                }
            }

            throw new Error(errorMessage);
        }

        let data;
        try {
            data = await response.json();
        } catch (jsonError) {
            console.error('Invalid JSON response:', jsonError);
            throw new Error('Invalid JSON response from API');
        }

        // Payoo B2C legacy flow: backend returns form data instead of a redirect URL.
        // Build a hidden <form>, populate the Payoo fields, and POST it to Payoo's paynow endpoint.
        if (paymentGatewayType === 'payoo' && data.payoo_form && data.payoo_form.url && data.payoo_form.data && data.payoo_form.checksum) {
            const form = document.createElement('form');
            form.id = 'frmPayByPayoo';
            form.name = 'frmPayByPayoo';
            form.method = 'POST';
            form.action = data.payoo_form.url;
            const fields: Record<string, string> = {
                bc: '',
                pm: '',
                OrdersForPayoo: data.payoo_form.data,
                CheckSum: data.payoo_form.checksum,
            };
            Object.entries(fields).forEach(([name, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            return null;
        }

        if (!data.verify) {
            let errorMessage = 'Client secret not found in response';

            // Show detailed response if available
            if (data.message) {
                errorMessage = data.message;
            } else if (data.error) {
                errorMessage = data.error;
            } else if (data.detail) {
                errorMessage = data.detail;
            } else if (Object.keys(data).length > 0) {
                errorMessage = `${errorMessage} - Response: ${JSON.stringify(data)}`;
            }

            throw new Error(errorMessage);
        }
        if (paymentGatewayType === 'stripe_js') {
            this.clientSecret = data.verify;
        } else {
            if (data.verify && (data.verify.startsWith('http://') || data.verify.startsWith('https://'))) {
                window.location.href = data.verify;
                return null;
            }
        }
        this.id = data.id || null;
        this.type = config.payment_type || null;
        this.apiResponse = data || null;
        this.currency = data.currency || null;
        this.amount = data.amount !== undefined && data.amount !== null ? Number(data.amount) : null;
        this.beforeDiscountAmount = data.before_discount_amount !== undefined && data.before_discount_amount !== null ? Number(data.before_discount_amount) : null;
        this.firstCycleAmount = data.first_cycle_amount !== undefined && data.first_cycle_amount !== null ? Number(data.first_cycle_amount) : null;

        // Extract trial information from API response
        this.isTrial = data.is_trial !== undefined && data.is_trial !== null ? Boolean(data.is_trial) : null;
        this.nextBillingDate = data.next_bill !== undefined && data.next_bill !== null ? Number(data.next_bill) : null;
        // Ensure we have a valid client secret before returning
        if (!this.clientSecret) {
            throw new Error('Invalid client secret received from API');
        }

        return this.clientSecret;
    }

    getLastCheckoutTaxSynced(): string | null {
        return this.lastCheckoutTaxSynced;
    }

    resetCheckoutTaxSync(): void {
        this.lastCheckoutTaxSynced = null;
    }

    /**
     * PATCH VAT metadata on the pending subscription / invoice / payment intent (no new purchase request).
     */
    async updateCheckoutTaxInfo(config: PaymentConfig, taxInfo: TaxInfo): Promise<void> {
        const checkoutId = this.id;
        if (!checkoutId) {
            throw new Error('Checkout is not ready; wait for payment form to load');
        }
        const shouldUseGuest = !config.access_token ||
            config.access_token.trim() === '' ||
            isBase64String(config.access_token);
        const apiUrl = this.getApiUrl(`${shouldUseGuest ? 'guest' : 'account'}/checkout-tax-info`);
        const {authToken, authHeaderName} = this.getAuthTokenAndHeader(config);
        const tax_info: Record<string, string> = {
            tax_number: taxInfo.tax_number,
            company_name: taxInfo.company_name,
            company_address: taxInfo.company_address,
            invoice_email: taxInfo.invoice_email
        };
        if (taxInfo.buyer_name) {
            tax_info.buyer_name = taxInfo.buyer_name;
        }
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'accept': 'application/json',
                [authHeaderName]: authToken,
                'store': 'stripe',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({id: checkoutId, tax_info})
        });
        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: Failed to update tax info`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.detail) {
                    errorMessage = typeof errorData.detail === 'string'
                        ? errorData.detail
                        : JSON.stringify(errorData.detail);
                } else {
                    errorMessage = `${errorMessage} - ${JSON.stringify(errorData)}`;
                }
            } catch {
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = `${errorMessage} - ${errorText}`;
                    }
                } catch { /* keep */ }
            }
            throw new Error(errorMessage);
        }
        this.lastCheckoutTaxSynced = JSON.stringify(taxInfo);
    }

    /**
     * Initialize Stripe instance
     */
    async initializeStripe(): Promise<Stripe> {
        if (this.stripe) {
            return this.stripe;
        }

        const publishableKey = await this.fetchPublishableKey();
        this.stripe = await loadStripe(publishableKey);

        if (!this.stripe) {
            throw new Error('Failed to initialize Stripe');
        }

        return this.stripe;
    }

    /**
     * Create Stripe elements with default configuration
     */
    async createElements(
        config: PaymentConfig
    ): Promise<StripeElements> {
        if (!this.stripe) {
            await this.initializeStripe();
        }

        if (!this.clientSecret) {
            await this.fetchClientSecret(config);
        }

        if (!this.stripe || !this.clientSecret) {
            throw new Error('Stripe or client secret not available');
        }

        this.elements = this.stripe.elements({
            clientSecret: this.clientSecret,
            locale: (config.locale || 'auto') as any
        });

        return this.elements;
    }

    /**
     * Create and mount payment element with default configuration
     */
    async createPaymentElement(
        mountSelector: string,
        config: PaymentConfig,
        elementsConfig?: Partial<StripeElementsConfig>
    ): Promise<void> {
        if (!this.elements) {
            await this.createElements(config);
        }

        if (!this.elements) {
            throw new Error('Elements not available');
        }

        const defaultConfig: StripeElementsConfig & { defaultValues?: { billingDetails?: { email?: string; phone?: string } } } = {
            theme: 'flat',
            layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: true,
                spacedAccordionItems: true,
                visibleAccordionItemsCount: 100
            },
            wallets: {
                applePay: "auto",
                googlePay: "auto",
                link: "auto"
            },
            business: {
                name: 'ELSA Speak',
            },
            fields: {
                billingDetails: {
                    email: 'never',
                    address: {
                        country: 'never'
                    }
                }
            },
            ...(config.email || config.phone ? {
                defaultValues: {
                    billingDetails: {
                        ...(config.email && { email: config.email }),
                        ...(config.phone && { phone: config.phone })
                    }
                }
            } : {}),
            ...elementsConfig
        };

        const paymentElement = this.elements.create('payment', defaultConfig as any);
        await paymentElement.mount(mountSelector);
    }

    /**
     * Create and mount link authentication element
     */
    async createLinkAuthenticationElement(
        mountSelector: string,
        email?: string
    ): Promise<void> {
        if (!this.elements) {
            throw new Error('Elements not available. Call createElements first.');
        }

        const linkAuthenticationElement = this.elements.create('linkAuthentication', {
            defaultValues: {
                email: email || '',
            }
        });

        await linkAuthenticationElement.mount(mountSelector);
    }

    /**
     * Wait for DOM elements to be available with retry logic
     */
    async waitForDOMElements(selectors: string[], maxRetries = 20, delay = 100): Promise<void> {
        let retryCount = 0;

        return new Promise((resolve, reject) => {
            const checkElements = () => {
                const elements = selectors.map(selector => {
                    const element = document.querySelector(selector);
                    if (!element) {
                        console.warn(`Element not found for selector: ${selector}`);
                    }
                    return element;
                });

                const allElementsFound = elements.every(el => el !== null);

                if (allElementsFound) {
                    console.log('All required DOM elements found');
                    resolve();
                } else if (retryCount < maxRetries) {
                    retryCount++;
                    console.log(`Waiting for DOM elements, attempt ${retryCount}/${maxRetries}. Missing selectors: ${
                        selectors.filter((selector, index) => elements[index] === null).join(', ')
                    }`);
                    setTimeout(checkElements, delay);
                } else {
                    const missingSelectors = selectors.filter((selector, index) => elements[index] === null);
                    const errorMessage = `DOM elements not found after ${maxRetries} retries. Missing selectors: ${missingSelectors.join(', ')}`;
                    console.error(errorMessage);
                    reject(new Error(errorMessage));
                }
            };

            // Add a small initial delay to allow DOM to be ready
            setTimeout(checkElements, 50);
        });
    }

    /**
     * Confirm payment with Stripe
     */
    async confirmPayment(
        returnUrl?: string,
        callbacks?: StripeServiceCallbacks
    ): Promise<{ error?: string; data?: unknown }> {
        if (!this.stripe || !this.elements) {
            const error = 'Stripe or Elements not initialized';
            callbacks?.onError?.({message: error});
            return {error};
        }

        try {
            callbacks?.onLoading?.(true);

            // Always prepare billing details since we set email to 'never' in Elements
            // Stripe requires billing_details.email to be passed when email field is set to 'never'
            const billingDetails: any = {
                email: this.config.email || '', // Always include email, even if empty
                address: {
                    country: 'US' // Always include country since we set it to 'never' in Elements
                }
            };

            // Check if client secret is for setup intent (starts with "seti_")
            const isSetupIntent = this.clientSecret?.startsWith('seti_');

            // Use appropriate Stripe method based on intent type
            if (isSetupIntent) {
                const confirmParams: any = {
                    return_url: returnUrl || window.location.href,
                    payment_method_data: {
                        billing_details: billingDetails
                    }
                };

                const result = await this.stripe.confirmSetup({
                    elements: this.elements,
                    confirmParams,
                    redirect: 'if_required',
                });

                const {error, setupIntent} = result;

                if (error) {
                    if (error.code === 'setup_intent_unexpected_state' &&
                        (error as any).setup_intent?.status === 'succeeded') {
                        const alreadySucceededId = this.id || (error as any).setup_intent?.id;
                        if (alreadySucceededId) {
                            const verificationData = await this.verifyPaymentStatus(alreadySucceededId);
                            callbacks?.onSuccess?.(verificationData);
                            return {data: verificationData};
                        }
                    }
                    const errorMessage = error.message || 'An unexpected error occurred';
                    callbacks?.onError?.({message: errorMessage});
                    return {error: errorMessage};
                }
                if (setupIntent && setupIntent.status === 'succeeded') {
                    try {
                        const verificationData = await this.verifyPaymentStatus(this.id || setupIntent.id);
                        callbacks?.onSuccess?.(verificationData);
                        return {data: verificationData};
                    } catch (verificationError) {
                        const errorMessage = verificationError instanceof Error
                            ? verificationError.message
                            : 'Setup verification failed';
                        console.error('Setup verification failed:', verificationError);
                        callbacks?.onError?.({message: errorMessage});
                        return {error: errorMessage};
                    }
                }

                const errorMessage = 'Setup not completed';
                callbacks?.onError?.({message: errorMessage});
                return {error: errorMessage};

            } else {
                const confirmParams: any = {
                    return_url: returnUrl || window.location.href,
                    payment_method_data: {
                        billing_details: billingDetails
                    }
                };

                const result = await this.stripe.confirmPayment({
                    elements: this.elements,
                    confirmParams,
                    redirect: 'if_required',
                });

                const {error, paymentIntent} = result;

                if (error) {
                    // If the PaymentIntent already succeeded (e.g. duplicate confirm), treat as success
                    if (error.code === 'payment_intent_unexpected_state' &&
                        (error as any).payment_intent?.status === 'succeeded') {
                        const alreadySucceededId = this.id || (error as any).payment_intent?.id;
                        if (alreadySucceededId) {
                            const verificationData = await this.verifyPaymentStatus(alreadySucceededId);
                            callbacks?.onSuccess?.(verificationData);
                            return {data: verificationData};
                        }
                    }
                    const errorMessage = error.message || 'An unexpected error occurred';
                    callbacks?.onError?.({message: errorMessage});
                    return {error: errorMessage};
                }

                if (paymentIntent && paymentIntent.status === 'succeeded') {
                    try {
                        const verificationData = await this.verifyPaymentStatus(paymentIntent.id);
                        callbacks?.onSuccess?.(verificationData);
                        return {data: verificationData};
                    } catch (verificationError) {
                        const errorMessage = verificationError instanceof Error
                            ? verificationError.message
                            : 'Payment verification failed';
                        console.error('Payment verification failed:', verificationError);
                        callbacks?.onError?.({message: errorMessage});
                        return {error: errorMessage};
                    }
                }

                const errorMessage = 'Payment not completed';
                callbacks?.onError?.({message: errorMessage});
                return {error: errorMessage};
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            callbacks?.onError?.({message: errorMessage});
            return {error: errorMessage};
        } finally {
            callbacks?.onLoading?.(false);
        }
    }

    /**
     * Get Stripe instance
     */
    getStripe(): Stripe | null {
        return this.stripe;
    }

    /**
     * Get Elements instance
     */
    getElements(): StripeElements | null {
        return this.elements;
    }

    /**
     * Get client secret
     */
    getClientSecret(): string {
        if (!this.clientSecret) {
            throw new Error('Client secret not available. Call fetchClientSecret first.');
        }
        return this.clientSecret;
    }

    /**
     * Get ID
     */
    getId(): string | null {
        return this.id;
    }

    /**
     * Get full API response
     */
    getApiResponse(): ApiResponse | null {
        return this.apiResponse;
    }

    /**
     * Get publishable key
     */
    getPublishableKey(): string | null {
        return this.publishableKey;
    }

    /**
     * Get currency from API response
     */
    getCurrency(): string | null {
        return this.currency;
    }

    getAmount(): number | null {
        return this.amount;
    }

    getFirstCycleAmount(): number | null {
        return this.firstCycleAmount;
    }

    getBeforeDiscountAmount(): number | null {
        return this.beforeDiscountAmount;
    }

    /**
     * Clear cached payment state and re-fetch with updated promotion config.
     * Preserves Stripe instance and publishable key to avoid full re-initialization.
     */
    async refetchWithPromotion(config: PaymentConfig): Promise<string | null> {
        this.clientSecret = null;
        this.elements = null;
        this.id = null;
        this.apiResponse = null;
        this.currency = null;
        this.amount = null;
        this.beforeDiscountAmount = null;
        this.firstCycleAmount = null;
        this.isTrial = null;
        this.nextBillingDate = null;
        this.lastCheckoutTaxSynced = null;
        return this.fetchClientSecret(config, 'stripe_js', true);
    }

    /**
     * Verify a promotion code against the server before applying it.
     * Throws on invalid/expired codes so the caller can show an error.
     */
    async verifyPromotionCode(promotionCode: string): Promise<{ valid: boolean; discount: Record<string, any> }> {
        const apiUrl = this.getApiUrl('payments/verify-promotion');
        const body: Record<string, string> = { promotion_code: promotionCode };
        if (this.config.product_id) {
            body.product_id = this.config.product_id;
        }
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            let errorMessage = 'Invalid or expired promotion code';
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
            } catch { /* keep default */ }
            throw new Error(errorMessage);
        }
        return response.json();
    }

    /**
     * Reset the service state
     */
    reset(): void {
        this.stripe = null;
        this.elements = null;
        this.clientSecret = null;
        this.publishableKey = null;
        this.id = null;
        this.apiResponse = null;
        this.currency = null;
        this.amount = null;
        this.beforeDiscountAmount = null;
        this.firstCycleAmount = null;
        this.isTrial = null;
        this.nextBillingDate = null;
        this.lastCheckoutTaxSynced = null;
    }

    /**
     * Verify payment status with the backend API
     */
    private async verifyPaymentStatus(transactionId: string): Promise<any> {
        try {
            const {authToken, authHeaderName} = this.getAuthTokenAndHeader(this.config);

            const verificationUrl = this.getVerifyStatusUrl(transactionId);

            const response = await fetch(verificationUrl, {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                    [authHeaderName]: authToken,
                    'store': 'stripe'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to verify payment status`);
            }

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                console.error('Invalid JSON response during verification:', jsonError);
                throw new Error('Invalid JSON response from verification API');
            }

            return data;
        } catch (error) {
            console.error('Failed to verify payment status:', error);
            throw new Error('Failed to verify payment status with backend');
        }
    }

    /**
     * Determine authentication token and header based on available credentials
     */
    private getAuthTokenAndHeader(config: PaymentConfig): { authToken: string; authHeaderName: string } {
        if (config.access_token) {
            // Use access_token directly with x-session-token header
            const authToken = config.access_token;
            let authHeaderName = 'x-session-token';
            if (isBase64String(config.access_token)) {
                authHeaderName = 'x-user-token';
            }
            return {authToken, authHeaderName};
        } else if (config.user_id || config.email) {
            // Fall back to encoding user_id or email with timestamp using x-user-token header
            const tokenIdentifier = config.user_id || config.email;
            const timestamp = Date.now();
            const authToken = btoa(`${tokenIdentifier} ${timestamp}`);
            const authHeaderName = 'x-user-token';
            return {authToken, authHeaderName};
        } else {
            throw new Error('Either access_token, user_id, or email is required for authentication');
        }
    }

    /**
     * Get API URL with base URL and path
     */
    private getApiUrl(path: string): string {
        const baseUrl = BASE_URLS[this.config.environment];
        return `${baseUrl}/${path}`;
    }

    /**
     * Get the appropriate payment URL based on payment type and access token
     */
    private getPaymentUrl(): string {
        const shouldUseGuest = !this.config.access_token ||
            this.config.access_token.trim() === '' ||
            isBase64String(this.config.access_token);
        const baseEndpoint = shouldUseGuest ? 'guest' : 'account';
        if (this.config.product_id === 'dynamic') {
            return this.getApiUrl(`${baseEndpoint}/dynamic-payment`);
        }
        if (this.config.payment_type === 'subscription') {
            return this.getApiUrl(`${baseEndpoint}/subscription`);
        }
        return this.getApiUrl(`${baseEndpoint}/payment`);
    }

    /**
     * Get the appropriate verification URL based on payment type and access token
     */
    private getVerifyStatusUrl(transactionId: string): string {
        const shouldUseGuest = !this.config.access_token ||
            this.config.access_token.trim() === '' ||
            isBase64String(this.config.access_token);
        const baseEndpoint = shouldUseGuest ? 'guest' : 'account';
        const verificationPath = this.config.payment_type === 'subscription'
            ? `${baseEndpoint}/subscription/${transactionId}`
            : `${baseEndpoint}/payment/${transactionId}`;
        return this.getApiUrl(verificationPath);
    }

    public getTrialInfo(): { isTrial: boolean | null; nextBillingDate: number | null } {
        return {
            isTrial: this.isTrial,
            nextBillingDate: this.nextBillingDate
        };
    }
}
